<script setup>
import { ref } from 'vue';

// Avatar with "Change photo" and, when the user has their own photo,
// "Remove photo". Emits the picked file or the removal; the parent saves it
// and passes the new `src` back.
defineProps({
  src: { type: String, default: '' },
  uploading: { type: Boolean, default: false },
  removable: { type: Boolean, default: false },
  removing: { type: Boolean, default: false },
});
const emit = defineEmits(['select', 'remove']);

const inputRef = ref(null);

function onChange(event) {
  const [file] = event.target.files || [];
  if (file) emit('select', file);
  event.target.value = ''; // picking the same file again still fires
}
</script>

<template>
  <div class="photo">
    <v-avatar size="64" class="photo__avatar">
      <v-img v-if="src" :src="src" alt="Profile photo" cover />
      <v-icon v-else>mdi-account</v-icon>
    </v-avatar>
    <div class="photo__body">
      <div class="photo__actions">
        <v-btn variant="outlined" size="small" :loading="uploading" :disabled="removing" @click="inputRef.click()">
          Change photo
        </v-btn>
        <v-btn
          v-if="removable"
          variant="text"
          size="small"
          :loading="removing"
          :disabled="uploading"
          @click="emit('remove')"
        >
          Remove photo
        </v-btn>
      </div>
      <p class="photo__hint">JPG or PNG, up to 5 MB.</p>
    </div>
    <input
      ref="inputRef"
      type="file"
      accept="image/png, image/jpeg"
      class="photo__input"
      @change="onChange"
    />
  </div>
</template>

<style scoped>
.photo {
  display: flex;
  align-items: center;
  gap: 16px;
}
.photo__avatar {
  border: 1px solid var(--border);
  background: var(--accent);
  color: var(--faint-foreground);
}
.photo__body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.photo__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.photo__hint {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.photo__input {
  display: none;
}
</style>
